import { A11yModule, LiveAnnouncer } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, ElementRef, computed, signal } from '@angular/core';
import { ListExportScope, ListExportStep, NotificationTypes } from 'src/app/models/enums';
import { AuthService, ListExportService, UtilsService, WebSocketService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { images } from 'src/images';
import { TooltipModule } from '../tooltip/tooltip.module';
import { TooltipPosition } from '../tooltip/tooltip-position';

@Component({
  selector: 'app-list-export',
  templateUrl: './list-export.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [A11yModule, TooltipModule],
  // Its own socket, so an export keeps its progress while the page runs other operations
  providers: [WebSocketService],
  host: { '(document:click)': 'onDocumentClick($event)' },
})
export class ListExportComponent {
  readonly images = images;
  readonly ListExportScope = ListExportScope;
  readonly ListExportStep = ListExportStep;
  readonly TooltipPosition = TooltipPosition;

  readonly step = signal(ListExportStep.IDLE);
  readonly scope = signal(ListExportScope.ALL);
  readonly percentage = signal(0);

  readonly exportableList = this.listExportService.exportableList;
  readonly rowCounts = computed(() => this.exportableList()?.rowCounts ?? null);
  readonly isVisible = computed(() => !!this.exportableList() || this.step() === ListExportStep.EXPORTING);
  readonly isPopoverOpen = computed(() => this.step() === ListExportStep.CHOOSING && !!this.exportableList());
  readonly formattedRowCounts = computed(() => {
    const rowCounts = this.rowCounts();
    if (!rowCounts) return null;
    const numberFormat = new Intl.NumberFormat(this.literalService.currentLanguage());
    return { allRows: numberFormat.format(rowCounts.allRows), visibleRows: numberFormat.format(rowCounts.visibleRows) };
  });
  readonly selectedRows = computed(() => {
    const rowCounts = this.rowCounts();
    if (!rowCounts) return 0;
    return this.scope() === ListExportScope.ALL ? rowCounts.allRows : rowCounts.visibleRows;
  });

  constructor(
    public literalService: LiteralService,
    private listExportService: ListExportService,
    private webSocketService: WebSocketService,
    private authService: AuthService,
    private utilsService: UtilsService,
    private liveAnnouncer: LiveAnnouncer,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  onDocumentClick(event: MouseEvent) {
    if (this.isPopoverOpen() && !this.elementRef.nativeElement.contains(event.target as Node)) this.closePopover();
  }

  togglePopover() {
    if (this.isPopoverOpen()) {
      this.closePopover();
      return;
    }
    this.scope.set(ListExportScope.ALL);
    this.step.set(ListExportStep.CHOOSING);
  }

  closePopover() {
    if (this.step() === ListExportStep.CHOOSING) this.step.set(ListExportStep.IDLE);
  }

  async startExport() {
    const exportableList = this.exportableList();
    if (!exportableList || !this.selectedRows()) return;
    this.percentage.set(0);
    this.step.set(ListExportStep.EXPORTING);
    try {
      const operationId = await exportableList.startExport(this.scope());
      const outcome = await this.webSocketService.trackOperation(
        operationId,
        await this.ephemeralToken(),
        (message) => this.updatePercentage(message),
        () => this.ephemeralToken(),
      );
      if (outcome.error) {
        this.showExportError(outcome.error);
        return;
      }
      this.percentage.set(100);
      await this.listExportService.download(outcome.result as string);
      this.liveAnnouncer.announce(this.literalService.get('listExport.downloaded', true));
    } finally {
      this.step.set(ListExportStep.IDLE);
    }
  }

  private updatePercentage(message: any) {
    if (typeof message?.total_percentage === 'number') this.percentage.set(message.total_percentage);
  }

  private async ephemeralToken(): Promise<string> {
    return (await this.authService.getEphemeralToken()) as string;
  }

  private showExportError(error: any) {
    const key = error?.error_code ? `${error.error_code}_ERROR` : 'listExport.error';
    this.utilsService.showNotification(images.notifications.warning, key, NotificationTypes.DANGER);
  }
}
